import React, { useState, useMemo } from 'react';
import { Form, Button, Alert, Spinner, Card } from 'react-bootstrap';
import { createOfferWithAssetWallet } from '../../services/p2pAPI';
import { getUserWallets } from '../../services/api';
import GenericButton from '../updated/shared/Button';
import tokensList from '../../utils/Tokens.json';
import './OfferCreate.css';

const CreateOfferForm: React.FC = () => {
  const [formData, setFormData] = useState({
    type: 'BUY' as 'BUY' | 'SELL',
    baseToken: '',      // Currency user will RECEIVE
    quoteToken: '',     // Currency user will SEND
    price: '',
    minAmount: '',
    maxAmount: '',
    availableAmount: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [walletTokens, setWalletTokens] = useState<any[]>([]);

  // Get all tokens from exchange routes (Tokens.json)
  const allTokens = useMemo(() => {
    const tokenSymbols = tokensList.map((token: any) => token.title);
    // Add USD to the list
    return ['USD', ...tokenSymbols].sort();
  }, []);

  // Create a map of token balances from wallet
  const tokenBalances = useMemo(() => {
    const balanceMap: { [key: string]: number } = {};
    walletTokens.forEach((token: any) => {
      const symbol = token.coinSymbol?.toUpperCase() || '';
      if (symbol) {
        balanceMap[symbol] = token.coinBalance || 0;
      }
    });
    return balanceMap;
  }, [walletTokens]);

  // Format balance for display
  const formatBalance = (balance: number) => {
    if (balance >= 1) {
      return balance.toFixed(4);
    } else {
      return balance.toFixed(8);
    }
  };

  // Filter tokens for baseToken dropdown (exclude selected quoteToken) and sort by balance
  const baseTokenOptions = useMemo(() => {
    const filtered = allTokens.filter(token => token !== formData.quoteToken);
    // Sort: tokens with balance first, then tokens without balance
    return filtered.sort((a, b) => {
      const balanceA = tokenBalances[a.toUpperCase()];
      const balanceB = tokenBalances[b.toUpperCase()];
      const hasBalanceA = balanceA !== undefined && balanceA > 0;
      const hasBalanceB = balanceB !== undefined && balanceB > 0;

      if (hasBalanceA && !hasBalanceB) return -1; // A has balance, B doesn't - A comes first
      if (!hasBalanceA && hasBalanceB) return 1;  // B has balance, A doesn't - B comes first
      return 0; // Both have or both don't have - keep original order
    });
  }, [allTokens, formData.quoteToken, tokenBalances]);

  // Filter tokens for quoteToken dropdown (exclude selected baseToken)
  // Note: No balance check needed for quoteToken - it will be sent by other users (counterparties)
  const quoteTokenOptions = useMemo(() => {
    return allTokens.filter(token => token !== formData.baseToken);
  }, [allTokens, formData.baseToken]);

  React.useEffect(() => {
    loadWalletTokens();
  }, []);

  const loadWalletTokens = async () => {
    try {
      const userEmail = localStorage.getItem('user');
      if (!userEmail) return;

      const response = await getUserWallets(userEmail);
      if (response.status === 200 && response.data) {
        const activeTokens = response.data.filter((token: any) =>
          token.isCoinActive && token.coinBalance > 0
        );
        setWalletTokens(activeTokens);
      }
    } catch (error) {
      console.error('Failed to load wallet tokens:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // If type changes, clear baseToken and quoteToken so user must reselect
    if (name === 'type') {
      setFormData(prev => ({
        ...prev,
        type: value as 'BUY' | 'SELL',
        baseToken: '',
        quoteToken: '',
      }));
    } else if (name === 'baseToken') {
      // If baseToken changes and it matches quoteToken, clear quoteToken
      setFormData(prev => ({
        ...prev,
        [name]: value,
        quoteToken: prev.quoteToken === value ? '' : prev.quoteToken,
      }));
    } else if (name === 'quoteToken') {
      // If quoteToken changes and it matches baseToken, clear baseToken
      setFormData(prev => ({
        ...prev,
        [name]: value,
        baseToken: prev.baseToken === value ? '' : prev.baseToken,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    setError('');
    setSuccess(false);
  };

  const validateForm = () => {
    if (!formData.baseToken) {
      setError('Please select the currency you will receive');
      return false;
    }
    if (!formData.quoteToken) {
      setError('Please select the currency you will send');
      return false;
    }
    if (formData.baseToken === formData.quoteToken) {
      setError('Base token and quote token must be different');
      return false;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      setError('Please enter a valid price');
      return false;
    }
    if (!formData.availableAmount || parseFloat(formData.availableAmount) <= 0) {
      setError('Please enter a valid available amount');
      return false;
    }
    if (!formData.minAmount || parseFloat(formData.minAmount) <= 0) {
      setError('Please enter a valid minimum amount');
      return false;
    }
    if (!formData.maxAmount || parseFloat(formData.maxAmount) <= 0) {
      setError('Please enter a valid maximum amount');
      return false;
    }
    if (parseFloat(formData.minAmount) >= parseFloat(formData.maxAmount)) {
      setError('Minimum amount must be less than maximum amount');
      return false;
    }
    if (parseFloat(formData.minAmount) > parseFloat(formData.availableAmount)) {
      setError('Minimum amount cannot be greater than available amount');
      return false;
    }
    if (parseFloat(formData.maxAmount) > parseFloat(formData.availableAmount)) {
      setError('Maximum amount cannot be greater than available amount');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const offerData = {
        type: formData.type,
        baseToken: formData.baseToken,
        quoteToken: formData.quoteToken,
        price: parseFloat(formData.price),
        minAmount: parseFloat(formData.minAmount),
        maxAmount: parseFloat(formData.maxAmount),
        availableAmount: parseFloat(formData.availableAmount),
      };

      const response = await createOfferWithAssetWallet(offerData);

      console.log('Create offer response:', response);

      if (response.success || response.data) {
        setSuccess(true);
        console.log('Offer created successfully!', response.data);

        // Reset form
        setFormData({
          type: 'BUY' as 'BUY' | 'SELL',
          baseToken: '',
          quoteToken: '',
          price: '',
          minAmount: '',
          maxAmount: '',
          availableAmount: '',
        });

        // Reload wallet tokens
        await loadWalletTokens();

        // Show success message with offer details
        console.log('Offer created with ID:', response.data?.id || response.data?.offerId || 'N/A');
        console.log('Offer status:', response.data?.status || 'active');
      }
    } catch (error: any) {
      console.error('Failed to create offer:', error);
      setError(error?.response?.data?.message || error?.message || 'Failed to create offer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-offer-form-container">
      <Card className="offer-create-card">
        <Card.Header className="offer-create-card-header">
          <h4>Create New Offer</h4>
        </Card.Header>
        <Card.Body className="offer-create-card-body">
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <Alert.Heading>Error</Alert.Heading>
              <p>{error}</p>
            </Alert>
          )}

          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess(false)}>
              <Alert.Heading>Success!</Alert.Heading>
              <p>Your offer has been created successfully.</p>
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            {/* Offer Type */}
            <div className="form-section">
              <div className="section-title">
                <i className="fas fa-exchange-alt me-2"></i>
                Offer Type
              </div>
              <Form.Group className="mb-3">
                <Form.Label className="form-label">Type</Form.Label>
                <Form.Select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="form-select-custom"
                  required
                >
                  <option value="BUY">Buy</option>
                  <option value="SELL">Sell</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  {formData.type === 'BUY'
                    ? 'You want to BUY cryptocurrency by paying USD'
                    : 'You want to SELL cryptocurrency to receive USD'}
                </Form.Text>
              </Form.Group>
            </div>

            {/* Currency Selection */}
            <div className="form-section">
              <div className="section-title">
                <i className="fas fa-coins me-2"></i>
                Currency Details
              </div>
              <div className="row">
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label className="form-label">
                    Base Token (Currency You Will RECEIVE)
                  </Form.Label>
                  <Form.Select
                    name="baseToken"
                    value={formData.baseToken}
                    onChange={handleChange}
                    className="form-select-custom"
                    required
                  >
                    <option value="">Select token</option>
                    {baseTokenOptions.map(token => {
                      const balance = tokenBalances[token.toUpperCase()];
                      const hasBalance = balance !== undefined && balance > 0;
                      const balanceText = balance !== undefined && balance > 0
                        ? ` (Balance: ${formatBalance(balance)} ${token})`
                        : '';
                      return (
                        <option
                          key={token}
                          value={token}
                          disabled={!hasBalance}
                          style={{
                            color: hasBalance ? 'inherit' : '#999',
                            fontStyle: hasBalance ? 'normal' : 'italic'
                          }}
                        >
                          {token}{balanceText}
                        </option>
                      );
                    })}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    The currency you will receive {formData.baseToken && tokenBalances[formData.baseToken.toUpperCase()] !== undefined && (
                      <span className="text-success">
                        - Available: {formatBalance(tokenBalances[formData.baseToken.toUpperCase()])} {formData.baseToken}
                      </span>
                    )}
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label className="form-label">
                    Quote Token (Currency You Will SEND)
                  </Form.Label>
                  <Form.Select
                    name="quoteToken"
                    value={formData.quoteToken}
                    onChange={handleChange}
                    className="form-select-custom"
                    required
                  >
                    <option value="">Select token</option>
                    {quoteTokenOptions.map(token => (
                      <option key={token} value={token}>{token}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    The currency you will send (will be sent by other users when they join your offer)
                  </Form.Text>
                </Form.Group>
              </div>
            </div>

            {/* Payment Method - Display Only (Asset Wallet) */}
            <div className="form-section">
              <div className="section-title">
                <i className="fas fa-wallet me-2"></i>
                Payment Method
              </div>
              <Form.Group className="mb-3">
                <Form.Label className="form-label">Payment Method</Form.Label>
                <Form.Control
                  type="text"
                  value="Asset Wallet"
                  className="form-control-custom"
                  disabled
                  readOnly
                  style={{ backgroundColor: 'var(--main-body)', opacity: 0.7, cursor: 'not-allowed' }}
                />
                <Form.Text className="text-muted">
                  <i className="fas fa-info-circle me-1"></i>
                  Offers can only be created with Asset Wallet payment method
                </Form.Text>
              </Form.Group>
            </div>

            {/* Price and Amount */}
            <div className="form-section">
              <div className="section-title">
                <i className="fas fa-dollar-sign me-2"></i>
                Price & Amount
              </div>
              <div className="row">
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label className="form-label">Price</Form.Label>
                  <Form.Control
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    className="form-control-custom"
                    placeholder="$0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                  <Form.Text className="text-muted">
                    Price per unit of {formData.baseToken || 'base token'} in {formData.quoteToken || 'quote token'}
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label className="form-label">Available Amount ({formData.baseToken || 'Base Token'})</Form.Label>
                  <Form.Control
                    type="number"
                    name="availableAmount"
                    value={formData.availableAmount}
                    onChange={handleChange}
                    className="form-control-custom"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                  <Form.Text className="text-muted">
                    Total amount available for this offer
                  </Form.Text>
                </Form.Group>
              </div>
              <div className="row">
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label className="form-label">Minimum Amount ({formData.baseToken || 'Base Token'})</Form.Label>
                  <Form.Control
                    type="number"
                    name="minAmount"
                    value={formData.minAmount}
                    onChange={handleChange}
                    className="form-control-custom"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                  <Form.Text className="text-muted">
                    Minimum amount per transaction
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label className="form-label">Maximum Amount ({formData.baseToken || 'Base Token'})</Form.Label>
                  <Form.Control
                    type="number"
                    name="maxAmount"
                    value={formData.maxAmount}
                    onChange={handleChange}
                    className="form-control-custom"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                  <Form.Text className="text-muted">
                    Maximum amount per transaction
                  </Form.Text>
                </Form.Group>
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <GenericButton
                text={loading ? 'Creating...' : 'Create Offer'}
                onClick={handleSubmit}
                IconComponent={loading ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="fas fa-plus-circle me-2"></i>}
                className=""
                styles={{}}
                disabled={loading}
                loading={loading}
              />
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default CreateOfferForm;

